import React, { useState, useRef, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { FiX, FiUploadCloud, FiCheck, FiArrowRight, FiAlertTriangle } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Modal from '../../components/Modal';
import Button from '../../components/Button';
import Select from '../../components/Select';
import Api from '../../Api';
import './ImportEmployeesModal.css';

const DB_FIELDS = [
  { value: 'name', label: 'Nome / Colaborador' },
  { value: 'cpf', label: 'CPF' },
  { value: 'email', label: 'E-mail' },
  { value: 'station_ref', label: 'Posto / Lotação' },
  { value: 'position_ref', label: 'Cargo' },
  { value: 'journey_ref', label: 'Jornada' },
  { value: 'registration_number', label: 'Matrícula' },
  { value: 'phone', label: 'Telefone / Celular' },
  { value: 'admission_date', label: 'Data Admissão' },
  { value: 'birth_date', label: 'Data Nascimento' },
  { value: 'rg', label: 'RG' },
  { value: 'rg_issuer', label: 'RG Órgão Emissor' },
  { value: 'rg_issue_date', label: 'RG Data Emissão' },
  { value: 'pis_pasep', label: 'PIS/PASEP' },
  { value: 'ctps_number', label: 'CTPS Número' },
  { value: 'ctps_series', label: 'CTPS Série' },
  { value: 'address_zip_code', label: 'CEP' },
  { value: 'address_street', label: 'Endereço (Logradouro)' },
  { value: 'address_number', label: 'Número' },
  { value: 'address_complement', label: 'Complemento' },
  { value: 'address_district', label: 'Bairro' },
  { value: 'address_city', label: 'Cidade' },
  { value: 'address_state', label: 'UF' },
  { value: 'salary', label: 'Salário' },
  { value: 'company_ref', label: 'Empresa (ID ou Nome)' },
  { value: 'sex', label: 'Sexo' },
];

export default function ImportEmployeesModal({ isOpen, onClose, onSuccess, companyId, companies = [] }) {
  const [step, setStep] = useState('upload'); // upload, map
  const [fileData, setFileData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({}); // { headerName: dbField }
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(companyId || '');

  // Dependencies for mapping (Stations, Positions, Journeys)
  const [stations, setStations] = useState([]);
  const [positions, setPositions] = useState([]);
  const [journeys, setJourneys] = useState([]);

  useEffect(() => {
    setSelectedCompanyId(companyId || '');
  }, [companyId]);

  useEffect(() => {
    if (isOpen && selectedCompanyId) {
      loadDependencies(selectedCompanyId);
    }
  }, [isOpen, selectedCompanyId]);

  const loadDependencies = async (cId) => {
    try {
      const [stationsData, positionsData, journeysData] = await Promise.all([
        Api.getServiceStations(cId),
        Api.getPositions(cId),
        Api.getJourneys(cId)
      ]);
      setStations(Array.isArray(stationsData) ? stationsData : []);
      setPositions(Array.isArray(positionsData) ? positionsData : []);
      setJourneys(Array.isArray(journeysData) ? journeysData : []);
    } catch (error) {
      console.error('Erro ao carregar dependências:', error);
      // Don't block import, just log
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1 }); // Array of arrays

        if (data.length < 2) {
          toast.warning('O arquivo parece estar vazio ou sem dados.');
          return;
        }

        // Find header row (similar logic to old component but simplified)
        let headerRowIndex = 0;
        // Basic check if first row is empty or not header-like
        if (data[0].length === 0) {
           // try to find first non-empty row
           headerRowIndex = data.findIndex(row => row.length > 0);
           if (headerRowIndex === -1) headerRowIndex = 0;
        }

        const fileHeaders = data[headerRowIndex].map(h => String(h).trim());
        const rows = data.slice(headerRowIndex + 1);

        setHeaders(fileHeaders);
        setFileData(rows);
        
        const initialMapping = {};
        fileHeaders.forEach(header => {
          const lower = normalizeString(header);
          if (lower.includes('empresa') || lower.includes('company') || lower.includes('companhia') || lower.includes('compania')) initialMapping[header] = 'company_ref';
          else if (lower.includes('nome') || lower.includes('colaborador') || lower.includes('funcionario') || lower.includes('employee') || lower.includes('nome completo')) initialMapping[header] = 'name';
          else if (lower.includes('cpf') || lower.includes('documento') || lower.includes('doc')) initialMapping[header] = 'cpf';
          else if (lower.includes('email') || lower.includes('e-mail') || lower.includes('mail')) initialMapping[header] = 'email';
          else if (lower.includes('telefone') || lower.includes('celular') || lower.includes('whatsapp') || lower.includes('cel') || lower.includes('fone')) initialMapping[header] = 'phone';
          else if (lower.includes('matricula') || lower.includes('registro') || lower.includes('id funcionario') || lower.includes('codigo')) initialMapping[header] = 'registration_number';
          else if (lower.includes('admissao') || lower.includes('dt adm') || lower.includes('data de admissao')) initialMapping[header] = 'admission_date';
          else if (lower.includes('nascimento') || lower.includes('dt nasc') || lower.includes('data de nascimento')) initialMapping[header] = 'birth_date';
          else if (lower === 'rg' || (lower.includes('rg') && !lower.includes('orgao') && !lower.includes('emissor') && !lower.includes('data'))) initialMapping[header] = 'rg';
          else if (lower.includes('orgao') || lower.includes('emissor') || lower.includes('expedidor')) initialMapping[header] = 'rg_issuer';
          else if (lower.includes('emissao') && lower.includes('rg')) initialMapping[header] = 'rg_issue_date';
          else if (lower.includes('pis') || lower.includes('pasep')) initialMapping[header] = 'pis_pasep';
          else if ((lower.includes('ctps') || lower.includes('carteira')) && lower.includes('numero')) initialMapping[header] = 'ctps_number';
          else if ((lower.includes('ctps') || lower.includes('carteira')) && (lower.includes('serie') || lower.includes('série'))) initialMapping[header] = 'ctps_series';
          else if (lower.includes('cep') || lower.includes('zip') || lower.includes('postal')) initialMapping[header] = 'address_zip_code';
          else if (lower.includes('endereco') || lower.includes('logradouro') || lower.includes('rua') || lower.includes('address')) initialMapping[header] = 'address_street';
          else if (lower.includes('numero') || lower.includes('nº') || lower.includes('num')) initialMapping[header] = 'address_number';
          else if (lower.includes('complemento') || lower.includes('compl')) initialMapping[header] = 'address_complement';
          else if (lower.includes('bairro') || lower.includes('district') || lower.includes('neighborhood')) initialMapping[header] = 'address_district';
          else if (lower.includes('cidade') || lower.includes('municipio') || lower.includes('município') || lower.includes('city')) initialMapping[header] = 'address_city';
          else if (lower.includes('uf') || lower.includes('estado') || lower.includes('state')) initialMapping[header] = 'address_state';
          else if (lower.includes('salario') || lower.includes('remuneracao') || lower.includes('remuneração')) initialMapping[header] = 'salary';
          else if (lower.includes('sexo') || lower.includes('genero') || lower.includes('gênero') || lower.includes('gender')) initialMapping[header] = 'sex';
          else if (lower.includes('posto') || lower.includes('lotacao') || lower.includes('lotação') || lower.includes('unidade') || lower.includes('filial') || lower.includes('setor')) initialMapping[header] = 'station_ref';
          else if (lower.includes('cargo') || lower.includes('funcao') || lower.includes('função') || lower.includes('position')) initialMapping[header] = 'position_ref';
          else if (lower.includes('jornada') || lower.includes('escala') || lower.includes('horario') || lower.includes('horário') || lower.includes('shift')) initialMapping[header] = 'journey_ref';
        });
        
        setMapping(initialMapping);
        setStep('map');
      } catch (error) {
        console.error(error);
        toast.error('Erro ao ler o arquivo.');
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleMappingChange = (header, dbField) => {
    setMapping(prev => ({ ...prev, [header]: dbField }));
  };

  const normalizeString = (str) => {
    if (!str) return '';
    return String(str).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
  };

  const findItem = (list, ref, fields = ['name']) => {
    if (!ref) return null;
    const normalizedRef = normalizeString(ref);
    
    // 1. Exact ID
    const exactId = list.find(item => String(item.id) === String(ref));
    if (exactId) return exactId;

    // 2. Fuzzy match
    return list.find(item => {
      return fields.some(field => {
        const val = normalizeString(item[field]);
        return val && (val.includes(normalizedRef) || normalizedRef.includes(val));
      });
    }) || null;
  };

  const parseDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value.toISOString().split('T')[0];
    if (typeof value === 'number') {
        // Excel serial date
        const date = new Date((value - (25567 + 2)) * 86400 * 1000);
        return date.toISOString().split('T')[0];
    }
    if (typeof value === 'string') {
        if (value.match(/^\d{2}\/\d{2}\/\d{4}/)) {
            const [day, month, year] = value.split('/');
            return `${year}-${month}-${day}`;
        }
    }
    return null; // fallback
  };

  const handleImport = async () => {
    try {
      setLoading(true);
      
      const employeesRaw = fileData.map((row) => {
        const item = {};
        let rowCompanyId = selectedCompanyId;
        let stationRef = null;
        let positionRef = null;
        let journeyRef = null;

        headers.forEach((header, idx) => {
          const dbField = mapping[header];
          if (!dbField) return;
          const value = row[idx];
          if (value === undefined || value === null) return;

          if (dbField === 'company_ref') {
            const matchedCompany = findItem(companies, value, ['trade_name', 'corporate_name', 'id']);
            if (matchedCompany) rowCompanyId = matchedCompany.id;
            else if (!isNaN(Number(value))) rowCompanyId = Number(value);
          } else if (dbField === 'station_ref') {
            stationRef = value;
          } else if (dbField === 'position_ref') {
            positionRef = value;
          } else if (dbField === 'journey_ref') {
            journeyRef = value;
          } else if (dbField === 'admission_date' || dbField === 'birth_date' || dbField === 'rg_issue_date') {
            item[dbField] = parseDate(value);
          } else if (dbField === 'cpf') {
            item[dbField] = String(value).replace(/\D/g, '');
          } else if (dbField === 'address_zip_code') {
            item[dbField] = String(value).replace(/\D/g, '');
          } else if (dbField === 'salary') {
            item[dbField] = typeof value === 'string' ? parseFloat(value.replace(',', '.')) : value;
          } else if (dbField === 'sex') {
            item[dbField] = String(value).toLowerCase().startsWith('m') ? 'male' : 'female';
          } else {
            item[dbField] = value;
          }
        });

        item.company_id = rowCompanyId;
        item._stationRef = stationRef;
        item._positionRef = positionRef;
        item._journeyRef = journeyRef;
        item.type = 'C';
        item.is_active = 'true';
        item.status = 'Ativo';
        return item;
      }).filter(e => e.name);

      if (employeesRaw.length === 0) {
        toast.warning('Nenhum dado válido para importar (verifique Nome).');
        setLoading(false);
        return;
      }

      const withCompany = employeesRaw.filter(e => e.company_id);
      const withoutCompany = employeesRaw.filter(e => !e.company_id);

      if (withCompany.length === 0) {
        toast.error('Nenhuma empresa detectada. Selecione a empresa padrão ou mapeie a coluna "Empresa".');
        setLoading(false);
        return;
      }

      const uniqueCompanyIds = Array.from(new Set(withCompany.map(e => String(e.company_id)).filter(Boolean)));
      const depsMap = {};
      await Promise.all(uniqueCompanyIds.map(async (cId) => {
        try {
          const [s, p, j] = await Promise.all([
            Api.getServiceStations(cId),
            Api.getPositions(cId),
            Api.getJourneys(cId)
          ]);
          depsMap[cId] = {
            stations: Array.isArray(s) ? s : [],
            positions: Array.isArray(p) ? p : [],
            journeys: Array.isArray(j) ? j : [],
          };
        } catch (err) {
          depsMap[cId] = { stations: [], positions: [], journeys: [] };
        }
      }));

      const employeesToCreate = withCompany.map(e => {
        const deps = depsMap[String(e.company_id)] || { stations: [], positions: [], journeys: [] };
        if (e._stationRef) {
          const matched = findItem(deps.stations, e._stationRef, ['name', 'nickname']);
          if (matched) e.service_station_ids = [matched.id];
        }
        if (e._positionRef) {
          const matched = findItem(deps.positions, e._positionRef, ['name']);
          if (matched) e.position_id = matched.id;
        }
        if (e._journeyRef) {
          const matched = findItem(deps.journeys, e._journeyRef, ['name', 'description']);
          if (matched) e.journey_id = matched.id;
        }
        delete e._stationRef;
        delete e._positionRef;
        delete e._journeyRef;
        return e;
      });

      // Group by company
      const byCompany = employeesToCreate.reduce((acc, emp) => {
        if (!acc[emp.company_id]) acc[emp.company_id] = [];
        acc[emp.company_id].push(emp);
        return acc;
      }, {});

      let totalSuccess = 0;
      let totalSkipped = withoutCompany.length; // skipped due to missing company
      for (const cId of Object.keys(byCompany)) {
          const existing = await Api.getEmployees(cId);
          const existingCpfSet = new Set(
            (Array.isArray(existing) ? existing : [])
              .map(e => e && e.cpf ? String(e.cpf).replace(/\D/g, '') : null)
              .filter(Boolean)
          );
          const candidates = byCompany[cId].filter(emp => {
            const cpf = emp.cpf ? String(emp.cpf).replace(/\D/g, '') : null;
            if (!cpf) return true;
            return !existingCpfSet.has(cpf);
          });
          totalSkipped += byCompany[cId].length - candidates.length;
          if (candidates.length === 0) {
            continue;
          }
          const res = await Api.bulkCreateEmployees(cId, candidates);
          const arr = Array.isArray(res) ? res : [];
          if (arr.length > 0) {
            totalSuccess += arr.filter(r => r && r.status === 'success').length;
          }
      }
      
      toast.success(`${totalSuccess} funcionários importados com sucesso!`);
      if (totalSkipped > 0) {
        toast.info(`${totalSkipped} registro(s) ignorado(s) (empresa ausente ou CPF já existente).`);
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error(error);
      toast.error('Erro ao importar dados. Verifique o console.');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setStep('upload');
    setFileData([]);
    setHeaders([]);
    setMapping({});
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => { reset(); onClose(); }}
      title="Importar Funcionários"
      size="lg"
    >
      <div className="import-modal-body">
        <div className="import-content-area">
            {step === 'upload' && (
              <div className="upload-step-container">
                  {companies.length > 0 && (
                     <div className="company-select-container" style={{ marginBottom: '1rem' }}>
                         <Select
                             label="Selecione a empresa padrão para importação"
                             options={[{ value: '', label: 'Detectar do arquivo ou Selecione...' }, ...companies.map(c => ({ value: c.id, label: c.corporate_name || c.trade_name }))] }
                             value={selectedCompanyId}
                             onChange={e => setSelectedCompanyId(e.target.value)}
                             useCustomDropdown={true}
                             searchable
                         />
                     </div>
                  )}
                  <div 
                      className="upload-area"
                      onClick={() => {
                          fileInputRef.current.click();
                      }}
                  >
                    <FiUploadCloud className="upload-icon" />
                    <p className="upload-text">
                        Clique para selecionar um arquivo Excel (.xlsx) ou CSV
                    </p>
                    <p className="upload-subtext">O arquivo deve conter cabeçalhos na primeira linha.</p>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        accept=".xlsx, .xls, .csv"
                        style={{ display: 'none' }}
                    />
                </div>
              </div>
            )}

            {step === 'map' && (
                <div className="map-step">
                    <p className="step-description">Mapeie as colunas do seu arquivo para os campos do sistema.</p>
                    
                    <div className="mapping-grid">
                        {headers.map((header, idx) => (
                            <div key={idx} className="mapping-row">
                                <div className="file-header">
                                    <span className="label">Coluna do Arquivo:</span>
                                    <strong>{header}</strong>
                                    {fileData[0] && (
                                        <span className="example-value">Ex: {fileData[0][idx]}</span>
                                    )}
                                </div>
                                <div className="arrow-icon">
                                    <FiArrowRight />
                                </div>
                                <div className="db-field-select">
                                    <Select
                                        options={[{ value: '', label: 'Ignorar' }, ...DB_FIELDS]}
                                        value={mapping[header] || ''}
                                        onChange={(e) => handleMappingChange(header, e.target.value)}
                                        placeholder="Selecione o campo..."
                                        useCustomDropdown={true}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>

        <div className="modal-actions">
            <Button 
                onClick={() => step === 'upload' ? onClose() : reset()} 
                className="cancel-button"
                disabled={loading}
            >
                {step === 'upload' ? 'Cancelar' : 'Voltar'}
            </Button>
            
            {step === 'map' && (
                <Button onClick={handleImport} disabled={loading}>
                    {loading ? 'Importando...' : 'Importar Dados'}
                </Button>
            )}
        </div>
      </div>
    </Modal>
  );
}
